import React from 'react'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Stack, Typography } from '@mui/material_modern'
import { makeStyles } from '@mui/styles'
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'

import { setDecimalPrecision } from 'utils/helpers'
import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'
import { MODAL_VIEW_MODES } from 'utils/constants'
import FORM_MODALS from 'utils/ui/modals'
import { showFormModal } from 'state/ui/index'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
  },
}))

const getColumns = (dispatch, defaultYardTimezone = null, defaultYardUm) => [
  {
    field: 'materialName',
    headerName: 'Material',
    flex: 2,
    sortable: false,
  },
  {
    field: 'netWeight',
    headerName: 'Net Weight',
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    renderCell: ({ row = {} }) => {
      const { earliestWeight, lowestWeight, currentWeight } = row
      return (
        <Stack width="100%">
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontStyle="italic">
              Earliest:
            </Typography>
            <Typography variant="body2">{`${setDecimalPrecision(earliestWeight, { decimalPrecisionOverride: 1, removeTrailingZeros: true })} ${defaultYardUm}`}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontStyle="italic">
              Lowest:
            </Typography>
            <Typography variant="body2">{`${setDecimalPrecision(lowestWeight, { decimalPrecisionOverride: 1, removeTrailingZeros: true })} ${defaultYardUm}`}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontStyle="italic">
              Current:
            </Typography>
            <Typography variant="body2">{`${setDecimalPrecision(currentWeight, { decimalPrecisionOverride: 1, removeTrailingZeros: true })} ${defaultYardUm}`}</Typography>
          </Stack>
        </Stack>
      )
    },
    sortable: false,
  },
  {
    field: 'dateOfActivity',
    headerName: 'Date of Activity (Weight)',
    flex: 2,
    renderCell: ({ row = {} }) => {
      const { earliestDate, lowestDate } = row
      return (
        <Stack width="100%">
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">
              {dateTimeFormatter(earliestDate, { timeZone: defaultYardTimezone })}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">{dateTimeFormatter(lowestDate, { timeZone: defaultYardTimezone })}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {'\n'}
            </Typography>
          </Stack>
        </Stack>
      )
    },
    sortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 2,
    renderCell: ({ row = {} }) => {
      const { materialTypeId, earliestDate, earliestWeight } = row
      return (
        <Stack flexDirection="row" justifyContent="space-between">
          <Button
            onClick={() => {
              dispatch(
                showFormModal({
                  formModalName: FORM_MODALS.NEW_REGRADE,
                  formModalProps: {
                    viewMode: MODAL_VIEW_MODES.NEW,
                    initialMaterialTypeId: materialTypeId,
                    initialEffectiveDate: dayjs(earliestDate).subtract(10, 'ms').toISOString(),
                    initialNewWeight: Number(earliestWeight * -1),
                  },
                }),
              )
            }}
          >
            Regrade
          </Button>
          <Button
            onClick={() => {
              dispatch(
                showFormModal({
                  formModalName: FORM_MODALS.NEW_ADJUSTMENT,
                  formModalProps: {
                    viewMode: MODAL_VIEW_MODES.NEW,
                    initialMaterialTypeId: materialTypeId,
                    initialEffectiveDate: dayjs(earliestDate).subtract(10, 'ms').toISOString(),
                    initialNewWeight: Number(earliestWeight * -1),
                  },
                }),
              )
            }}
          >
            Adjust Inventory
          </Button>
        </Stack>
      )
    },
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
]

const NegativeInventoryListGrid = props => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { defaultYardTimezone } = useSelector(state => state.auth)
  const { defaultYardUm } = useSelector(state => state.settings)

  const columns = getColumns(dispatch, defaultYardTimezone, defaultYardUm)

  return <DataGridWithSearchV2 {...props} className={classes.root} columns={columns} rowHeight={100} />
}

NegativeInventoryListGrid.propTypes = DataGridWithSearchV2.propTypes

NegativeInventoryListGrid.defaultProps = DataGridWithSearchV2.defaultProps

export default NegativeInventoryListGrid
